import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X, Minus, Plus, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import GiftLines from "@/components/GiftLines";
import CartSaveContact from "@/components/CartSaveContact";

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal } = useCart();
  const { t } = useLang();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 !h-screen">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-xd-border">
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            {t("cart_title")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("cart_title")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-xd-muted">
              <div className="text-4xl mb-3">🛒</div>
              <p className="text-sm">{t("cart_empty")}</p>
              <Link to="/catalogo" onClick={() => setOpen(false)} className="mt-4 inline-block text-xd-cyanHover text-sm font-medium hover:underline">{t("cart_empty_cta")} →</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((i) => (
                <div key={i.cart_key} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 items-start pb-4 border-b border-xd-border last:border-b-0">
                  <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight line-clamp-2">{i.name}</div>
                    {i.variant_title && i.variant_title !== "Default Title" && (
                      <div className="text-[11px] text-xd-cyanHover mt-0.5 font-medium break-words whitespace-normal">{i.variant_title}</div>
                    )}
                    <div className="text-xs text-xd-muted mt-1 font-mono-tech">€ {i.price.toFixed(2)}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button type="button" onClick={() => update(i.cart_key, i.quantity - 1)} className="w-7 h-7 rounded border border-xd-border flex items-center justify-center hover:border-xd-cyan"><Minus className="w-3 h-3" /></button>
                      <span data-testid={`cart-qty-${i.product_id}`} className="text-sm w-6 text-center font-mono-tech">{i.quantity}</span>
                      <button type="button" onClick={() => update(i.cart_key, i.quantity + 1)} className="w-7 h-7 rounded border border-xd-border flex items-center justify-center hover:border-xd-cyan"><Plus className="w-3 h-3" /></button>
                      <button type="button" onClick={() => remove(i.cart_key)} data-testid={`cart-remove-${i.product_id}`} className="ml-auto text-xs text-xd-muted hover:text-xd-orangeHover flex items-center gap-1"><X className="w-3 h-3" />{t("cart_remove")}</button>
                    </div>
                  </div>
                  <div className="text-sm font-semibold font-display">€ {(i.price * i.quantity).toFixed(2)}</div>
                </div>
              ))}
              <GiftLines compact />
              <CartSaveContact />
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-4 border-t border-xd-border space-y-3 bg-white sticky bottom-0 shrink-0">
            <div className="flex justify-between text-sm">
              <span className="text-xd-muted">{t("cart_subtotal")}</span>
              <span className="font-semibold font-display" data-testid="cart-subtotal">€ {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium" data-testid="cart-free-shipping">
              <Truck className="w-3.5 h-3.5" />
              {t("cart_free_shipping")}
            </div>
            <Link to="/checkout" onClick={() => setOpen(false)}>
              <Button data-testid="cart-checkout-btn" className="w-full btn-cta text-white h-11 rounded-lg">{t("cart_checkout_btn")}</Button>
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
